import { db } from './firebase';
import { patientDocumentPath, patientDocumentRequest } from './patientDocumentApi';
import { collection, query, where, addDoc, getDoc, getDocs, doc, updateDoc, deleteDoc, arrayUnion, arrayRemove, collectionGroup, getCountFromServer, setDoc, Timestamp } from 'firebase/firestore';

// Add a new document to a collection
export const addData = async (collectionName, data) => {
    if (patientDocumentPath(collectionName)) return (await patientDocumentRequest(collectionName, 'POST', { data })).id;
    try {
        const docRef = await addDoc(collection(db, collectionName), data);
        return docRef.id;
    } catch (error) {
        console.error("Error adding document: ", error);
        throw error;
    }
};

// Set a document with a specific ID (creates or overwrites)
export const setData = async (collectionName, id, data) => {
    if (patientDocumentPath(collectionName)) return (await patientDocumentRequest(collectionName, 'POST', { data, documentId: id })).id;
    try {
        const docRef = doc(db, collectionName, id);
        await setDoc(docRef, data);
        return docRef.id;
    } catch (error) {
        console.error("Error setting document: ", error);
        throw error;
    }
};

// Get all documents from a collection
export const getDocuments = async (collectionName) => {
    if (patientDocumentPath(collectionName)) return (await patientDocumentRequest(collectionName))?.documents || [];
    try {
        const querySnapshot = await getDocs(collection(db, collectionName));
        const documents = querySnapshot.docs.map(doc => ({ 
            ...doc.data(), 
            id: doc.id  
        }));
        return documents;
    } catch (error) {
        console.error("Error getting documents: ", error);
        throw error;
    }
};
// Get data from a document
// Optional metadata may legitimately be absent. Keep read/permission failures
// visible even when callers opt out of the missing-document diagnostic.
export const getData = async (documentPath, { allowMissing = false } = {}) => {
    if (patientDocumentPath(documentPath)) return (await patientDocumentRequest(documentPath))?.document || null;
    try {
        const docRef = doc(db, documentPath);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        } else {
            // For user documents during signup, this might be expected
            if (documentPath.includes('/users/')) {
                console.log(`User document not found yet: ${documentPath}`);
            } else if (!allowMissing) {
                console.error("No such document!");
            }
            return null;
        }
    } catch (error) {
        // Handle permission errors more gracefully
        if (error.code === 'permission-denied') {
            console.warn(`Permission denied for document: ${documentPath}`);
            return null;
        }
        console.error("Error getting document: ", error);
        throw error;
    }
};

// Query documents by type
export const getDocumentsByType = async (collectionName, typeId) => {
    if (patientDocumentPath(collectionName)) return (await getDocuments(collectionName)).filter(document => document.typeId === typeId);
    try {
        const q = query(collection(db, collectionName), where("typeId", "==", typeId));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error("Error querying documents by type: ", error);
        throw error;
    }
};

// Update a document in a collection
export const updateData = async (collectionName, id, data) => {
    if (patientDocumentPath(collectionName)) return (await patientDocumentRequest(`${collectionName}/${id}`, 'PATCH', { data })).id;
    try {
        const docRef = doc(db, collectionName, id);
        await updateDoc(docRef, data);
        return docRef.id;
    } catch (error) {
        console.error("Error updating document: ", error);
        throw error;
    }
};

// Update a data with a function
export const updateDataFunction = async (documentPath, updateFunction) => {
    if (patientDocumentPath(documentPath)) return patientDocumentRequest(documentPath, 'PATCH', { data: updateFunction });
    try {
        const docRef = doc(db, documentPath);
        await updateDoc(docRef, updateFunction);
    } catch (error) {
        console.error("Error updating document: ", error);
        throw error;
    }
};

// Delete a document from a collection
export const deleteData = async (collectionName, id) => {
    try {
        const docRef = doc(db, collectionName, id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error("Error deleting document: ", error);
        throw error;
    }
};

// Simple cache for document requests (5 second TTL)
const documentCache = new Map();

// Get a single document with caching
export const getDocument = async (collectionName, documentId) => {
    // Do not cache private source across identities, revocation, or edits.
    if (patientDocumentPath(collectionName)) return (await patientDocumentRequest(`${collectionName}/${documentId}`))?.document || null;
    const cacheKey = `${collectionName}/${documentId}`;
    const now = Date.now();
    
    // Check cache first (5 second TTL)
    if (documentCache.has(cacheKey)) {
        const cached = documentCache.get(cacheKey);
        if (now - cached.timestamp < 5000) {
            return cached.data;
        }
        documentCache.delete(cacheKey);
    }
    
    try {
        const docRef = doc(db, collectionName, documentId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
            const data = { id: docSnap.id, ...docSnap.data() };
            
            // Cache the result
            documentCache.set(cacheKey, {
                data,
                timestamp: now
            });
            
            return data;
        } else {
            return null;
        }
    } catch (error) {
        console.error(`[firestoreService] Error getting document: ${error.message}`);
        throw error;
    }
};

// Update user settings
export const updateUserSettings = async (userId, newSettings) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
            const currentSettings = userDoc.data().settings || {};
            const updatedSettings = { ...currentSettings, ...newSettings };

            await updateDoc(userRef, {
                settings: updatedSettings
            });
        } else {
            console.error('No such user!');
        }
    } catch (error) {
        console.error('Error updating user settings:', error);
        throw error;
    }
};

// Settings toggles update one leaf atomically, preserving concurrent sibling
// preference writes without reading and replacing the whole settings map.
export const updateUserSetting = async (userId, field, value) => {
    if (!userId || typeof field !== 'string' || !/^[a-zA-Z][a-zA-Z0-9]*$/.test(field) || typeof value !== 'boolean') {
        throw new Error('Invalid user preference');
    }
    await updateDoc(doc(db, 'users', userId), { [`settings.${field}`]: value });
};

// Fetch user settings
export const getUserSettings = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
            return userDoc.data().settings || {};
        } else {
            console.log(`User document not found yet for settings: ${userId}`);
            return {};
        }
    } catch (error) {
        // Handle permission errors more gracefully
        if (error.code === 'permission-denied') {
            console.warn(`Permission denied for user settings: ${userId}`);
            return {};
        }
        console.error('Error fetching user settings:', error);
        return {};
    }
};

// array functions
export const removeFromArray = (field, valueToRemove) => {
    return {
        [field]: arrayRemove(valueToRemove),
    };
};

export const addToArray = (field, valueToAdd) => {
    return {
        [field]: arrayUnion(valueToAdd),
    };
};

// Update user's last login time
export const updateLastLoginTime = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        const now = new Date();
        
        await updateDoc(userRef, {
            lastLoginTime: now,
            lastLoginTimestamp: now.getTime() // Also store as timestamp for easier querying
        });
        
    } catch (error) {
        console.error('Error updating last login time:', error);
        // Don't throw error as this shouldn't block login
    }
};

// === Admin Functions ===

// Get all users (superAdmin only - enforced by Firestore rules)
export const getAllUsers = async () => {
  try {
    const usersCollection = collection(db, 'users');
    const querySnapshot = await getDocs(usersCollection);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

// Get recent login failures from audit logs (superAdmin only)
export const getRecentLoginFailures = async (days = 7) => {
  try {
    const cutoff = Timestamp.fromDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));
    const q = query(
      collection(db, 'auditLogs'),
      where('action', '==', 'auth.login.failure'),
      where('timestamp', '>=', cutoff)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data());
  } catch (error) {
    console.error('Error fetching login failures:', error);
    return [];
  }
};

// Update user by ID (superAdmin only - enforced by Firestore rules)
export const updateUserById = async (userId, data) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...data,
      modifiedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Get users by companyId (companyAdmin — scoped to own company)
export const getCompanyUsers = async (companyId) => {
  const q = query(collection(db, 'users'), where('companyId', '==', companyId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// === Admin Stats ===

export const getAdminStats = async () => {
  let patientCount = null; // null indicates unable to fetch
  let documentCount = null; 
  let sharedPatientCount = null;

  try {
    // 1. Try fast server-side counting for Patients
    try {
      const patientsColl = collection(db, 'patients');
      const patientsSnapshot = await getCountFromServer(patientsColl);
      patientCount = patientsSnapshot.data().count;
    } catch (err) {
      console.warn("Patient aggregation failed:", err);
      patientCount = null;
    }
    
    // 2. Try fast server-side counting for Documents
    try {
      const documentsColl = collectionGroup(db, 'documents');
      const documentsSnapshot = await getCountFromServer(documentsColl);
      documentCount = documentsSnapshot.data().count;
    } catch (err) {
      console.warn("Document aggregation failed:", err);
      documentCount = null; 
    }

    // 3. Shared-patient count — patients with a non-empty `sharedAccess` map.
    // Firestore has no native "map is non-empty" predicate; read patient docs
    // and filter client-side. Admin-only surface, acceptable for POC.
    try {
      const patientsSnap = await getDocs(collection(db, 'patients'));
      let shared = 0;
      patientsSnap.forEach((d) => {
        const sa = d.data()?.sharedAccess;
        if (sa && Object.keys(sa).length > 0) shared += 1;
      });
      sharedPatientCount = shared;
    } catch (err) {
      console.warn("Shared-patient count failed:", err);
      sharedPatientCount = null;
    }

    return {
      patientCount,
      documentCount,
      sharedPatientCount,
    };
  } catch (error) {
    console.error('Error fetching admin stats:', error);
    return { patientCount: null, documentCount: null, sharedPatientCount: null };
  }
};

// ---------------------------------------------------------------------------
// Template presets (globalSettings/templates)
// ---------------------------------------------------------------------------

/** Map entity type to the Firestore field key inside globalSettings/templates */
const PRESETS_FIELD_MAP = {
  company: 'companyPresets',
  user: 'userPresets',
  documentTypes: 'documentTypePresets',
};

/**
 * Fetch ALL presets for an entity type from Firestore templates.
 *
 * @param {'company'|'user'|'documentTypes'} entityType
 * @returns {Promise<Record<string, object>>} Map of presetId → preset object (empty on miss/error).
 */
export const getPresets = async (entityType) => {
  try {
    const templatesDoc = await getDoc(doc(db, 'globalSettings', 'templates'));
    if (!templatesDoc.exists()) return {};

    const data = templatesDoc.data();
    const fieldKey = PRESETS_FIELD_MAP[entityType] || `${entityType}Presets`;
    return data[fieldKey] || {};
  } catch (err) {
    console.error(`Error fetching ${entityType} presets:`, err);
    return {};
  }
};

/**
 * Fetch the default preset for an entity type from Firestore templates.
 *
 * @param {'company'|'user'|'documentTypes'} entityType
 * @returns {Promise<object|null>} The default preset object, or null on miss/error.
 */
export const getDefaultPreset = async (entityType) => {
  try {
    const templatesDoc = await getDoc(doc(db, 'globalSettings', 'templates'));
    if (!templatesDoc.exists()) return null;

    const data = templatesDoc.data();
    const fieldKey = PRESETS_FIELD_MAP[entityType] || `${entityType}Presets`;
    const presetsMap = data[fieldKey];
    if (!presetsMap) return null;

    // Find the preset marked isDefault, or fall back to the "default" key
    const preset =
      Object.values(presetsMap).find((p) => p.isDefault) ?? presetsMap.default ?? null;
    return preset;
  } catch (err) {
    console.error(`Error fetching ${entityType} template preset:`, err);
    return null;
  }
};
